import { useState, useEffect } from 'react';
import { ProjectSurveyResponseType } from '../../generated/graphql';

export const HelivanSurveyQuestion = ({
  question,
  initialResponse,
  onResponseChange,
}: {
  question: string;
  initialResponse?: ProjectSurveyResponseType;
  onResponseChange: (response: ProjectSurveyResponseType) => void;
}) => {
  const [selectedResponse, setSelectedResponse] =
    useState<ProjectSurveyResponseType | null>(initialResponse || null);

  // Update selectedResponse if initialResponse changes
  useEffect(() => {
    if (initialResponse) {
      setSelectedResponse(initialResponse);
    }
  }, [initialResponse]);

  const handleResponseClick = (responseValue: ProjectSurveyResponseType) => {
    setSelectedResponse(responseValue);
    if (onResponseChange) {
      onResponseChange(responseValue);
    }
  };

  const getCircleClasses = (
    responseValue: ProjectSurveyResponseType,
    index: number
  ) => {
    const isSelected = selectedResponse === responseValue;

    // Size mapping: largest for strongly agree/disagree, smaller for agree/disagree, smallest for neutral
    let sizeClasses;
    if (index === 0 || index === 4) {
      // Strongly disagree/agree - largest
      sizeClasses = 'w-12 h-12';
    } else if (index === 1 || index === 3) {
      // Disagree/agree - medium
      sizeClasses = 'w-9 h-9';
    } else {
      // Neutral - smallest
      sizeClasses = 'w-6 h-6';
    }

    const baseClasses = `${sizeClasses} rounded-full border-2 cursor-pointer transition-all duration-200 hover:scale-105`;

    // Color mapping based on position
    if (index <= 1) {
      // Disagree options - red/purple
      return `${baseClasses} ${
        isSelected
          ? 'bg-red-500 border-red-500 text-white'
          : 'border-red-500 hover:bg-red-50'
      }`;
    } else if (index === 2) {
      // Neutral - gray
      return `${baseClasses} ${
        isSelected
          ? 'bg-gray-500 border-gray-500 text-white'
          : 'border-gray-500 hover:bg-gray-50'
      }`;
    } else {
      // Agree options - green
      return `${baseClasses} ${
        isSelected
          ? 'bg-green-500 border-green-500 text-white'
          : 'border-green-500 hover:bg-green-50'
      }`;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8 bg-white">
      <div className="text-center mb-12">
        <h2 className="text-md font-normal text-gray-800 leading-relaxed max-w-3xl mx-auto">
          {question}
        </h2>
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-red-500 font-medium text-lg">Disagree</span>
        <span className="text-green-500 font-medium text-lg">Agree</span>
      </div>

      <div className="flex items-center justify-center space-x-14 mb-8">
        <button
          key={ProjectSurveyResponseType.StronglyDisagree}
          onClick={() =>
            handleResponseClick(ProjectSurveyResponseType.StronglyDisagree)
          }
          className={getCircleClasses(
            ProjectSurveyResponseType.StronglyDisagree,
            0
          )}
        >
          {selectedResponse === ProjectSurveyResponseType.StronglyDisagree && (
            <svg
              className={`mx-auto w-6 h-6`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>
        <button
          key={ProjectSurveyResponseType.Disagree}
          onClick={() =>
            handleResponseClick(ProjectSurveyResponseType.Disagree)
          }
          className={getCircleClasses(ProjectSurveyResponseType.Disagree, 1)}
        >
          {selectedResponse === ProjectSurveyResponseType.Disagree && (
            <svg
              className="mx-auto w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>
        <button
          key={ProjectSurveyResponseType.Neutral}
          onClick={() => handleResponseClick(ProjectSurveyResponseType.Neutral)}
          className={getCircleClasses(ProjectSurveyResponseType.Neutral, 2)}
        >
          {selectedResponse === ProjectSurveyResponseType.Neutral && (
            <svg
              className="mx-auto w-3 h-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>
        <button
          key={ProjectSurveyResponseType.Agree}
          onClick={() => handleResponseClick(ProjectSurveyResponseType.Agree)}
          className={getCircleClasses(ProjectSurveyResponseType.Agree, 3)}
        >
          {selectedResponse === ProjectSurveyResponseType.Agree && (
            <svg
              className="mx-auto w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>
        <button
          key={ProjectSurveyResponseType.StronglyAgree}
          onClick={() =>
            handleResponseClick(ProjectSurveyResponseType.StronglyAgree)
          }
          className={getCircleClasses(
            ProjectSurveyResponseType.StronglyAgree,
            4
          )}
        >
          {selectedResponse === ProjectSurveyResponseType.StronglyAgree && (
            <svg
              className="mx-auto w-6 h-6"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};
